"use client";

import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  Heading,
  Container,
  Card,
  CardBody,
  HStack,
  Icon,
  SimpleGrid,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckCircleIcon,
  StarIcon,
  PhoneIcon,
  MailIcon as EmailIcon,
  CatIcon as ChatIcon,
  BarChart3Icon,
} from "lucide-react";

const AppWelcome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleGetStarted = async () => {
    setIsLoading(true);
    // Simulate a brief loading state
    setTimeout(() => {
      navigate("/dashboard");
    }, 1000);
  };

  const features = [
    {
      icon: PhoneIcon,
      title: "AI Voice Agents",
      description: "Automated outbound and inbound call handling",
      badge: "Core Feature",
    },
    {
      icon: EmailIcon,
      title: "Multi-Channel Outreach",
      description: "Voice, SMS, Email, and WhatsApp campaigns",
      badge: "Popular",
    },
    {
      icon: ChatIcon,
      title: "Smart Scheduling",
      description: "Automated appointment booking and follow-ups",
      badge: "Time Saver",
    },
    {
      icon: BarChart3Icon,
      title: "Real-time Analytics",
      description: "Performance tracking and insights",
      badge: "Essential",
    },
  ];

  return (
    <Box
      minH="100vh"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="4xl">
        <Card boxShadow="xl" border="1px" borderColor="gray.200">
          <CardBody p={12}>
            <VStack spacing={8}>
              {/* Success Header */}
              <VStack spacing={4} textAlign="center">
                <Box>
                  <Icon as={CheckCircleIcon} w={16} h={16} color="green.500" />
                </Box>

                <Heading size="lg" color="secondary.800">
                  Welcome to
                </Heading>
                <Box>
                  <img
                    src={
                      process.env.REACT_APP_MAIN_LOGO_URL || "/placeholder.svg"
                    }
                    alt="App Logo"
                    style={{ height: "100px", width: "auto" }}
                  />
                </Box>
                {/* </CHANGE> */}
                <Text color="secondary.600" fontSize="lg">
                  Your account has been successfully set up, {user?.email}
                </Text>
              </VStack>

              <Divider />

              {/* Features Overview */}
              <VStack spacing={6} w="100%">
                <Heading size="md" color="secondary.800" textAlign="center">
                  You now have access to:
                </Heading>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
                  {features.map((feature, index) => (
                    <Card key={index} variant="outline" bg="gray.50">
                      <CardBody p={6}>
                        <VStack spacing={3} align="start">
                          <HStack justify="space-between" w="100%">
                            <HStack spacing={3}>
                              <Icon
                                as={feature.icon}
                                w={6}
                                h={6}
                                color="primary.500"
                              />
                              <Text fontWeight="semibold" color="secondary.800">
                                {feature.title}
                              </Text>
                            </HStack>
                            <Badge colorScheme="blue" size="sm">
                              {feature.badge}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="secondary.600" pl={9}>
                            {feature.description}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>
              </VStack>

              <Divider />

              {/* Next Steps */}
              <VStack spacing={4} w="100%">
                <Heading size="md" color="secondary.800" textAlign="center">
                  Ready to get started?
                </Heading>
                <Text color="secondary.600" textAlign="center" maxW="md">
                  Access your dashboard to set up your first AI agent, configure
                  campaigns, and start revolutionizing your outreach process.
                </Text>

                <Button
                  onClick={handleGetStarted}
                  isLoading={isLoading}
                  loadingText="Loading Dashboard..."
                  size="lg"
                  bg="primary.500"
                  color="white"
                  _hover={{ bg: "primary.600" }}
                  _active={{ bg: "primary.700" }}
                  leftIcon={<StarIcon size={20} />}
                  px={8}
                  py={6}
                >
                  Go to Dashboard
                </Button>
              </VStack>

              {/* Support Info */}
              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <Box bg="blue.50" p={4} borderRadius="md" w="100%">
                  <Text fontSize="sm" color="blue.700" textAlign="center">
                    Need help getting started? Contact our support team at{" "}
                    <Text as="span" fontWeight="semibold" color="primary.500">
                      support@leadiumx.ai
                    </Text>
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default AppWelcome;
